import React, { useEffect } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import {
    clearSelectedThreadMessage,
    fetchChatSession,
} from 'flow/actions/chat-actions';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { selectActiveMeetingChatOpen } from 'flow/selectors/meeting-selectors';
import { getExtensionKeyFromSession } from 'flow/selectors/selectors';
import { ChatSessionKey, ExtensionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { getChatShortcuts } from 'flow/utils/browser-utils';
import { isSMSChat } from 'flow/utils/message-utils';
import { isDirectMessageSession } from 'flow/utils/message-utils-TW';
import ChatBanner from './messages/ChatBanner';
import MessageInput from './messages/MessageInput';
import MessageList from 'flow/components/chat/v2/MessageList';
import NoChatProfileBanner from './messages/NoChatProfileBanner';
import MessagesPage from './MessagesPage';
import MessagesShortcuts from './MessagesShortcuts';

const Messages = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { id: chatSessionKey } = useParams<{ id: ChatSessionKey }>();
    const { isSinglePaneMode } = useSinglePaneMode();

    const [extensionKey, setExtensionKey] = React.useState<ExtensionKey | null>(
        null,
    );

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const session = useAppSelector((state) =>
        state.chats.getIn(['sessions', chatSessionKey]),
    );
    const chatIsOpenInMeeting = useAppSelector(selectActiveMeetingChatOpen);

    useEffect(() => {
        dispatch(fetchChatSession(chatSessionKey, 'contact'));
    }, []);

    useEffect(() => {
        dispatch(clearSelectedThreadMessage());
    }, [chatSessionKey, dispatch]);

    useEffect(() => {
        if (ownExtensionKey && session) {
            setExtensionKey(
                getExtensionKeyFromSession(session, ownExtensionKey),
            );
        }
    }, [ownExtensionKey, session]);

    const isNotSmsChat = !isSMSChat(session);
    const enableChatShortcuts = getChatShortcuts();

    const shouldShowChatShortcuts =
        isNotSmsChat &&
        enableChatShortcuts &&
        !chatIsOpenInMeeting &&
        !isSinglePaneMode;

    return (
        <>
            <MessagesPage
                onClickBack={
                    isSinglePaneMode
                        ? () => history.push(routes['messages'])
                        : undefined
                }
                header={session?.get('description') || ''}
                endButton={
                    <IconButton
                        className={shouldShowChatShortcuts && 'hidden'}
                        tooltipLabel={i18next.t('Chat.Details.GoToDetails')}
                        onClick={() =>
                            history.push(
                                generatePath(routes['messages.id.settings'], {
                                    id: chatSessionKey,
                                }),
                            )
                        }
                    >
                        <Icon iconKey="setting_menu" />
                    </IconButton>
                }
                footer={<MessageInput chatSession={chatSessionKey} />}
                chatSessionKey={chatSessionKey}
            >
                {isDirectMessageSession(session) && (
                    <div className="relative">
                        <div className="absolute top-6 z-30 flex w-full flex-col gap-2">
                            <NoChatProfileBanner session={session} />
                            <ChatBanner session={session} />
                        </div>
                    </div>
                )}
                <MessageList chatSessionKey={chatSessionKey} />
            </MessagesPage>

            {session && (
                <MessagesShortcuts
                    session={session}
                    extensionKey={extensionKey}
                    shouldShowChatShortcuts={shouldShowChatShortcuts}
                />
            )}
        </>
    );
};

export default Messages;
